import { useRef } from 'react';
import { BROADCAST_HISTORY_KEY } from './BroadcastHistory';
import { useQuery, useQueryClient } from 'react-query';
import { groupsApi } from '../../../services/groups-api-client';
import { scheduledBroadcastsKey } from '../../../lib/groupQueryKeys';
import { isScheduledBroadcastsUnavailable, scheduledBroadcastsShouldPoll, scheduledSendCompleted } from '../../../lib/scheduledBroadcasts';
import type { GroupScheduledBroadcast } from '../../../types/groups';

/**
 * The shared read behind BOTH the schedule controls and the pending list —
 * one react-query key ({@link scheduledBroadcastsKey}), so a schedule or a
 * cancel from either surface invalidates the same cache entry the other one
 * reads (see the module doc on `groupQueryKeys.ts`), and so mounting both
 * components costs exactly one GET, not two.
 *
 * Two behaviours that only make sense together with `lib/scheduledBroadcasts.ts`:
 *
 * - Polls while anything on the list is pending/sending
 *   ({@link scheduledBroadcastsShouldPoll}), because the background sweep
 *   fires due rows with no client action to react to.
 * - Never retries the 503 designed state
 *   ({@link isScheduledBroadcastsUnavailable}): an environment without
 *   migration 014 will not start succeeding on attempt 2, so retrying only
 *   delays showing "Scheduled sends aren't available on this environment yet."
 */
export interface ScheduledBroadcastsQuery {
  rows: GroupScheduledBroadcast[];
  isLoading: boolean;
  /** The 503 designed state. Callers hide/disable scheduling, never a red toast. */
  unavailable: boolean;
  /** Any OTHER failure (a real outage) — null once `unavailable` explains it. */
  error: unknown;
}

export function useScheduledBroadcasts(groupId: string): ScheduledBroadcastsQuery {
  const queryClient = useQueryClient();
  const previous = useRef<GroupScheduledBroadcast[] | undefined>(undefined);
  const query = useQuery(
    scheduledBroadcastsKey(groupId),
    () => groupsApi.listScheduledBroadcasts(groupId),
    {
      refetchInterval: (data) => (scheduledBroadcastsShouldPoll(data) ? 15_000 : false),
      retry: (failureCount, error) => !isScheduledBroadcastsUnavailable(error) && failureCount < 2,
      onSuccess: (rows) => {
        // A scheduled send fired since the last look: the Sent history is a
        // separate query and must hear about it — see scheduledSendCompleted.
        if (scheduledSendCompleted(previous.current, rows)) {
          queryClient.invalidateQueries(BROADCAST_HISTORY_KEY(groupId));
        }
        previous.current = rows;
      },
    },
  );

  const unavailable = isScheduledBroadcastsUnavailable(query.error);
  return {
    rows: query.data ?? [],
    isLoading: query.isLoading,
    unavailable,
    error: unavailable ? null : query.error,
  };
}
